import React, { useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playPeelSound, playWinFanfare } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { FiGift, FiAward, FiArrowUpRight } = FiIcons;

const PeelToRevealCard = ({ reward, onRevealed, compact = false }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const controls = useAnimation();

  const handleDragEnd = (event, info) => {
    // If dragged far enough to the right or bottom, peel it off completely
    if (info.offset.x > 80 || info.offset.y > 80 || info.offset.x < -80) {
      const settings = getSettings();
      if (settings.soundEnabled !== false) {
        playPeelSound();
        setTimeout(() => playWinFanfare(), 300);
      }

      controls.start({
        x: info.offset.x > 0 ? 500 : -500,
        y: info.offset.y > 0 ? 500 : -500,
        rotate: info.offset.x > 0 ? 45 : -45,
        opacity: 0,
        transition: { duration: 0.5, ease: "easeOut" }
      });
      
      setTimeout(() => {
        setIsRevealed(true);
        if (onRevealed) onRevealed();
      }, 500);
    } else {
      // Snap back if not dragged enough
      controls.start({ x: 0, y: 0, rotate: 0, transition: { type: "spring", stiffness: 300, damping: 20 } });
    }
  };

  return (
    <div className={`w-full ${compact ? 'max-w-[260px]' : 'max-w-[320px] sm:max-w-sm'} mx-auto aspect-[3/4] relative rounded-2xl shadow-2xl flex flex-col items-center justify-center select-none overflow-hidden touch-none`}>
      
      {/* Background (The Reward) */}
      <div className="absolute inset-0 z-10 bg-gradient-to-br from-amber-100 to-orange-200 border-4 border-slate-700 rounded-2xl flex flex-col items-center justify-center p-4 sm:p-6 text-center">
        <div className={`${compact ? 'w-16 h-16 mb-3' : 'w-22 h-22 mb-5'} rounded-full bg-white shadow-xl flex items-center justify-center`}>
          {reward.includes('Luck') || reward.includes('Try') ? (
            <SafeIcon icon={FiAward} className={`${compact ? 'text-3xl' : 'text-5xl'} text-slate-400`} />
          ) : (
            <SafeIcon icon={FiGift} className={`${compact ? 'text-3xl' : 'text-5xl'} text-orange-500`} />
          )}
        </div>
        <h4 className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-1">You Won</h4>
        <h2 className={`${compact ? 'text-xl' : 'text-2xl sm:text-3xl'} font-extrabold text-slate-800 leading-tight px-2`}>
          {reward}
        </h2>
      </div>

      {/* Foreground (The Sticker to Peel) */}
      <motion.div
        drag={!isRevealed}
        dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
        dragElastic={1}
        onDragEnd={handleDragEnd}
        animate={controls}
        whileTap={!isRevealed ? { scale: 0.98, cursor: "grabbing" } : {}}
        className={`absolute inset-0 z-20 w-full h-full origin-bottom-left ${isRevealed ? 'pointer-events-none' : 'cursor-grab'}`}
        style={{ touchAction: "none" }}
      >
          <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl border-4 border-white/20 shadow-[10px_10px_30px_rgba(0,0,0,0.5)] flex flex-col items-center justify-center relative overflow-hidden">
            
            {/* The folded corner effect */}
            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-white/40 to-transparent shadow-[-5px_5px_10px_rgba(0,0,0,0.2)] rounded-bl-3xl z-30 pointer-events-none"></div>
            
            <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80')] bg-cover mix-blend-overlay pointer-events-none"></div>
            
            <div className="relative z-10 flex flex-col items-center text-white pointer-events-none">
              <motion.div 
                animate={{ y: [-5, 5, -5], x: [5, -5, 5] }} 
                transition={{ repeat: Infinity, duration: 2 }}
                className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-4 border border-white/30"
              >
                <SafeIcon icon={FiArrowUpRight} className="text-3xl" />
              </motion.div>
              <h3 className="text-3xl font-black uppercase tracking-widest drop-shadow-md">Peel Me</h3>
              <p className="text-sm text-white/80 mt-2 font-medium">Drag to reveal prize</p>
            </div>

          </div>
        </motion.div>
    </div>
  );
};

export default PeelToRevealCard;